import { useSuspenseQuery } from "@tanstack/react-query";
import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router-dom";
import {
	countMetaAdsLeadFormCustomQuestions,
	MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS,
	type MetaAdsLeadFormDraft,
	type MetaAdsLeadFormQuestion,
	MetaAdsLeadFormQuestionType,
	type MetaAdsMetaLeadFormListItem,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { LinkGen, QueryKeys } from "../LinkGen";
import { useCampaignWizard } from "./CampaignWizardContext";
import { useLeadActions, useLeadForms, useMetaLeadForms } from "./useLeads";

type LeadFormMode = "create" | "meta";

const PageRequiredBanner: FC = () => {
	const company = useCompany();
	const { data: page } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.PageSingle(company.id),
		queryFn: async () => {
			try {
				return (await Api.GetMetaAdsPage()).data;
			} catch {
				return null;
			}
		},
	});

	if (page) return null;

	return (
		<div className="alert alert-warning" role="alert">
			A Facebook Page is required for Instant Forms. Select one in{" "}
			<Link to={LinkGen.Settings.Apps()}>Settings → Apps</Link>.
		</div>
	);
};

const PREDEFINED_FIELDS = [
	{
		type: MetaAdsLeadFormQuestionType.FULL_NAME,
		key: "full_name",
		label: "Full Name",
	},
	{
		type: MetaAdsLeadFormQuestionType.EMAIL,
		key: "email",
		label: "Email",
	},
	{
		type: MetaAdsLeadFormQuestionType.PHONE_NUMBER,
		key: "phone_number",
		label: "Phone Number",
	},
];

function createEmptyLeadForm(): MetaAdsLeadFormDraft {
	return {
		id: crypto.randomUUID(),
		name: "",
		headline: "",
		description: "",
		questions: [
			{
				type: MetaAdsLeadFormQuestionType.EMAIL,
				key: "email",
				label: "Email",
			},
		],
		privacyPolicyUrl: "",
		thankYouTitle: "Thank you!",
		thankYouDescription: "We will get back to you shortly.",
		thankYouButtonText: "Visit Website",
		thankYouButtonUrl: "",
	};
}

const FormPreview: FC<{ form: MetaAdsLeadFormDraft }> = ({ form }) => {
	const [showThankYou, setShowThankYou] = useState(false);

	return (
		<div
			style={{
				border: "1px solid #dee2e6",
				borderRadius: 12,
				padding: 20,
				maxWidth: 360,
				margin: "0 auto",
				background: "#fff",
			}}
		>
			<div
				style={{
					fontWeight: 600,
					fontSize: 16,
					marginBottom: 8,
					textAlign: "center",
				}}
			>
				{form.headline || form.name || "Untitled Form"}
			</div>
			{form.description && (
				<p
					style={{
						fontSize: 13,
						color: "#65676b",
						textAlign: "center",
						marginBottom: 12,
					}}
				>
					{form.description}
				</p>
			)}
			{!showThankYou ? (
				<>
					{form.questions.map((q) => (
						<div key={q.key} style={{ marginBottom: 10 }}>
							<span
								className="form-label d-block"
								style={{ fontSize: 13, marginBottom: 2 }}
							>
								{q.label}
							</span>
							<input
								type="text"
								className="form-control form-control-sm"
								disabled
								placeholder={q.label}
							/>
						</div>
					))}
					{form.privacyPolicyUrl && (
						<small className="text-muted d-block" style={{ marginBottom: 8 }}>
							By submitting, you agree to our{" "}
							<span style={{ textDecoration: "underline" }}>
								Privacy Policy
							</span>
						</small>
					)}
					<button
						type="button"
						className="btn btn-primary btn-sm w-100"
						onClick={() => setShowThankYou(true)}
					>
						Submit
					</button>
				</>
			) : (
				<div style={{ textAlign: "center" }}>
					<div style={{ fontWeight: 600, marginBottom: 4 }}>
						{form.thankYouTitle || "Thank you!"}
					</div>
					<p style={{ fontSize: 13, color: "#6c757d" }}>
						{form.thankYouDescription}
					</p>
					{form.thankYouButtonText && (
						<button
							type="button"
							className="btn btn-outline-primary btn-sm"
							onClick={() => setShowThankYou(false)}
						>
							{form.thankYouButtonText}
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export const StepLeadForm: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useCampaignWizard();
	const [error, setError] = useState("");
	const [mode, setMode] = useState<LeadFormMode>(
		draft.leadFormMetaId ? "meta" : "create",
	);

	const form: MetaAdsLeadFormDraft = draft.leadForm ?? createEmptyLeadForm();

	const switchMode = (next: LeadFormMode) => {
		setMode(next);
		setError("");
		if (next === "create") {
			updateDraft({
				leadFormMetaId: null,
				leadFormId: "",
				leadForm: draft.leadForm ?? createEmptyLeadForm(),
			});
		} else {
			updateDraft({
				leadFormMetaId: null,
				leadFormId: "",
				leadForm: null,
			});
		}
	};

	const updateForm = (partial: Partial<MetaAdsLeadFormDraft>) => {
		updateDraft({ leadForm: { ...form, ...partial } });
	};

	const isPredefinedActive = (key: string) =>
		form.questions.some((q) => q.key === key);

	const togglePredefined = (field: MetaAdsLeadFormQuestion) => {
		if (isPredefinedActive(field.key)) {
			updateForm({
				questions: form.questions.filter((q) => q.key !== field.key),
			});
		} else {
			updateForm({ questions: [...form.questions, field] });
		}
	};

	const addCustomQuestion = () => {
		if (
			countMetaAdsLeadFormCustomQuestions(form.questions) >=
			MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS
		) {
			return;
		}
		const key = `custom_${Date.now()}`;
		updateForm({
			questions: [
				...form.questions,
				{
					type: MetaAdsLeadFormQuestionType.CUSTOM,
					key,
					label: "",
				},
			],
		});
	};

	const updateCustomLabel = (key: string, label: string) => {
		updateForm({
			questions: form.questions.map((q) =>
				q.key === key ? { ...q, label } : q,
			),
		});
	};

	const removeQuestion = (key: string) => {
		updateForm({
			questions: form.questions.filter((q) => q.key !== key),
		});
	};

	const moveQuestion = (index: number, direction: -1 | 1) => {
		const newIndex = index + direction;
		if (newIndex < 0 || newIndex >= form.questions.length) return;
		const questions = [...form.questions];
		[questions[index], questions[newIndex]] = [
			questions[newIndex],
			questions[index],
		];
		updateForm({ questions });
	};

	const handleNext = () => {
		if (mode === "meta") {
			if (!draft.leadFormMetaId?.trim()) {
				setError("Select an Instant Form from Meta.");
				return;
			}
			setError("");
			nextStep();
			return;
		}

		if (form.questions.length === 0) {
			setError("At least one question is required.");
			return;
		}
		if (!form.privacyPolicyUrl.trim()) {
			setError("Privacy policy URL is required.");
			return;
		}
		if (!form.thankYouTitle.trim()) {
			setError("Thank you title is required.");
			return;
		}
		for (const q of form.questions) {
			if (q.type === MetaAdsLeadFormQuestionType.CUSTOM && !q.label.trim()) {
				setError("All custom questions must have a label.");
				return;
			}
		}
		if (
			countMetaAdsLeadFormCustomQuestions(form.questions) >
			MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS
		) {
			setError(
				`Instant Forms support at most ${MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS} custom questions.`,
			);
			return;
		}
		setError("");
		updateDraft({
			leadForm: form,
			leadFormId: form.id,
			leadFormMetaId: null,
		});
		nextStep();
	};

	return (
		<div style={{ display: "flex", gap: 24 }}>
			<div style={{ flex: 1 }}>
				<ErrorBoundary fallback={null}>
					<Suspense fallback={null}>
						<PageRequiredBanner />
					</Suspense>
				</ErrorBoundary>
				<Card>
					<Card.Header>Lead Form</Card.Header>
					<Card.Body>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 16,
							}}
						>
							<div>
								<p className="form-label" style={{ marginBottom: 8 }}>
									How would you like to set up the Instant Form?
								</p>
								<div className="d-flex gap-3 flex-wrap">
									<div className="form-check">
										<input
											className="form-check-input"
											type="radio"
											id="lead-form-mode-create"
											name="lead-form-mode"
											checked={mode === "create"}
											onChange={() => switchMode("create")}
										/>
										<label
											className="form-check-label"
											htmlFor="lead-form-mode-create"
										>
											Create new form
										</label>
									</div>
									<div className="form-check">
										<input
											className="form-check-input"
											type="radio"
											id="lead-form-mode-meta"
											name="lead-form-mode"
											checked={mode === "meta"}
											onChange={() => switchMode("meta")}
										/>
										<label
											className="form-check-label"
											htmlFor="lead-form-mode-meta"
										>
											Use existing form from Meta
										</label>
									</div>
								</div>
							</div>

							{mode === "meta" ? (
								<MetaLeadFormPicker
									selectedId={draft.leadFormMetaId ?? ""}
									onSelect={(metaForm) =>
										updateDraft({
											leadFormMetaId: metaForm.id,
											leadFormId: metaForm.id,
											leadForm: {
												id: metaForm.id,
												name: metaForm.name,
												headline: metaForm.name,
												description: "",
												questions: [],
												privacyPolicyUrl: "",
												thankYouTitle: "",
												thankYouDescription: "",
												thankYouButtonText: "",
												thankYouButtonUrl: "",
											},
										})
									}
								/>
							) : (
								<>
									<h6 className="mb-0">Form builder</h6>
									<div>
										<label className="form-label" htmlFor="form-name">
											Form Name
										</label>
										<input
											id="form-name"
											type="text"
											className="form-control"
											value={form.name}
											onChange={(e) => updateForm({ name: e.target.value })}
											placeholder="e.g. Contact Us Form"
										/>
									</div>

									<div>
										<label className="form-label" htmlFor="form-headline">
											Form Headline
										</label>
										<input
											id="form-headline"
											type="text"
											className="form-control"
											value={form.headline}
											onChange={(e) => updateForm({ headline: e.target.value })}
											placeholder="e.g. Get in touch today"
										/>
									</div>

									<div>
										<label className="form-label" htmlFor="form-description">
											Form Description
										</label>
										<textarea
											id="form-description"
											className="form-control"
											rows={2}
											value={form.description}
											onChange={(e) =>
												updateForm({ description: e.target.value })
											}
											placeholder="Brief description shown above the form fields"
										/>
									</div>

									<FormTemplateSelector
										onSelect={(template) =>
											updateDraft({
												leadForm: { ...template },
												leadFormId: template.id,
												leadFormMetaId: null,
											})
										}
									/>

									<h6>Questions</h6>
									<div>
										{PREDEFINED_FIELDS.map((field) => (
											<div className="form-check" key={field.key}>
												<input
													className="form-check-input"
													type="checkbox"
													id={`field-${field.key}`}
													checked={isPredefinedActive(field.key)}
													onChange={() => togglePredefined(field)}
												/>
												<label
													className="form-check-label"
													htmlFor={`field-${field.key}`}
												>
													{field.label}
												</label>
											</div>
										))}
									</div>

									{form.questions
										.filter(
											(q) => q.type === MetaAdsLeadFormQuestionType.CUSTOM,
										)
										.map((q) => {
											const idx = form.questions.findIndex(
												(fq) => fq.key === q.key,
											);
											return (
												<div
													key={q.key}
													style={{
														display: "flex",
														gap: 8,
														alignItems: "center",
													}}
												>
													<input
														type="text"
														className="form-control form-control-sm"
														value={q.label}
														onChange={(e) =>
															updateCustomLabel(q.key, e.target.value)
														}
														placeholder="Custom question label"
														style={{ flex: 1 }}
													/>
													<button
														type="button"
														className="btn btn-sm btn-outline-secondary"
														onClick={() => moveQuestion(idx, -1)}
														disabled={idx === 0}
													>
														&uarr;
													</button>
													<button
														type="button"
														className="btn btn-sm btn-outline-secondary"
														onClick={() => moveQuestion(idx, 1)}
														disabled={idx === form.questions.length - 1}
													>
														&darr;
													</button>
													<button
														type="button"
														className="btn btn-sm btn-outline-danger"
														onClick={() => removeQuestion(q.key)}
													>
														Remove
													</button>
												</div>
											);
										})}

									<button
										type="button"
										className="btn btn-outline-primary btn-sm"
										onClick={addCustomQuestion}
										disabled={
											countMetaAdsLeadFormCustomQuestions(form.questions) >=
											MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS
										}
									>
										+ Add Custom Question
									</button>
									{countMetaAdsLeadFormCustomQuestions(form.questions) >=
										MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS && (
										<p className="text-muted mt-2" style={{ fontSize: 13 }}>
											Up to {MAX_META_ADS_LEAD_FORM_CUSTOM_QUESTIONS} custom
											questions.
										</p>
									)}

									<h6>Privacy Policy</h6>
									<div>
										<label className="form-label" htmlFor="privacy-url">
											Privacy Policy URL
										</label>
										<input
											id="privacy-url"
											type="url"
											className="form-control"
											value={form.privacyPolicyUrl}
											onChange={(e) =>
												updateForm({ privacyPolicyUrl: e.target.value })
											}
											placeholder="https://example.com/privacy"
										/>
									</div>

									<h6>Thank You Screen</h6>
									<div>
										<label className="form-label" htmlFor="ty-title">
											Title
										</label>
										<input
											id="ty-title"
											type="text"
											className="form-control"
											value={form.thankYouTitle}
											onChange={(e) =>
												updateForm({ thankYouTitle: e.target.value })
											}
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="ty-desc">
											Description
										</label>
										<textarea
											id="ty-desc"
											className="form-control"
											rows={2}
											value={form.thankYouDescription}
											onChange={(e) =>
												updateForm({ thankYouDescription: e.target.value })
											}
										/>
									</div>
									<div style={{ display: "flex", gap: 12 }}>
										<div style={{ flex: 1 }}>
											<label className="form-label" htmlFor="ty-btn-text">
												Button Text
											</label>
											<input
												id="ty-btn-text"
												type="text"
												className="form-control"
												value={form.thankYouButtonText}
												onChange={(e) =>
													updateForm({ thankYouButtonText: e.target.value })
												}
											/>
										</div>
										<div style={{ flex: 1 }}>
											<label className="form-label" htmlFor="ty-btn-url">
												Button URL
											</label>
											<input
												id="ty-btn-url"
												type="url"
												className="form-control"
												value={form.thankYouButtonUrl}
												onChange={(e) =>
													updateForm({ thankYouButtonUrl: e.target.value })
												}
											/>
										</div>
									</div>

									<SaveAsTemplateButton form={form} />

									{error && <div className="text-danger">{error}</div>}

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
										}}
									>
										<button
											type="button"
											className="btn btn-outline-secondary"
											onClick={prevStep}
										>
											Back
										</button>
										<button
											type="button"
											className="btn btn-primary"
											onClick={handleNext}
										>
											Next
										</button>
									</div>
								</>
							)}

							{mode === "meta" && (
								<>
									{error && <div className="text-danger">{error}</div>}
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
										}}
									>
										<button
											type="button"
											className="btn btn-outline-secondary"
											onClick={prevStep}
										>
											Back
										</button>
										<button
											type="button"
											className="btn btn-primary"
											onClick={handleNext}
										>
											Next
										</button>
									</div>
								</>
							)}
						</div>
					</Card.Body>
				</Card>
			</div>

			<div style={{ width: 400, flexShrink: 0 }}>
				<Card>
					<Card.Header>Preview</Card.Header>
					<Card.Body>
						{mode === "meta" && draft.leadFormMetaId ? (
							<div style={{ textAlign: "center", padding: 16 }}>
								<p style={{ fontWeight: 600, marginBottom: 8 }}>
									{draft.leadForm?.name || "Meta Instant Form"}
								</p>
								<p className="text-muted" style={{ fontSize: 13 }}>
									Using an existing form on your connected Facebook Page. Field
									layout and thank-you screen are managed in Meta.
								</p>
								<p className="text-muted" style={{ fontSize: 12 }}>
									Form ID: {draft.leadFormMetaId}
								</p>
							</div>
						) : (
							<FormPreview form={form} />
						)}
					</Card.Body>
				</Card>
			</div>
		</div>
	);
};

const MetaLeadFormPicker: FC<{
	selectedId: string;
	onSelect: (form: MetaAdsMetaLeadFormListItem) => void;
}> = ({ selectedId, onSelect }) => {
	const {
		data: forms,
		isLoading,
		isError,
		refetch,
		isFetching,
	} = useMetaLeadForms();

	if (isLoading) {
		return <p className="text-muted">Loading Instant Forms from Meta…</p>;
	}

	if (isError) {
		return (
			<div className="alert alert-warning mb-0">
				Could not load Instant Forms. Confirm Meta Ads is connected and a
				Facebook Page is selected in{" "}
				<Link to={LinkGen.Settings.Apps()}>Settings → Apps</Link>.
			</div>
		);
	}

	if (!forms?.length) {
		return (
			<div className="alert alert-info mb-0">
				No active Instant Forms found on your connected Facebook Page. Create
				one in Meta Business Suite, or choose &quot;Create new form&quot; above.
			</div>
		);
	}

	return (
		<div>
			<label className="form-label" htmlFor="meta-lead-form-select">
				Select Instant Form
			</label>
			<div className="d-flex gap-2 align-items-start flex-wrap">
				<Select
					id="meta-lead-form-select"
					className="form-select"
					style={{ maxWidth: 420 }}
					value={selectedId}
					onChange={(e) => {
						const form = forms.find((f) => f.id === e.target.value);
						if (form) onSelect(form);
					}}
				>
					<option value="">-- Select a form --</option>
					{forms.map((f) => (
						<option key={f.id} value={f.id}>
							{f.name}
						</option>
					))}
				</Select>
				<button
					type="button"
					className="btn btn-outline-secondary btn-sm"
					onClick={() => void refetch()}
					disabled={isFetching}
				>
					{isFetching ? "Refreshing…" : "Refresh"}
				</button>
			</div>
			<p className="text-muted mt-2" style={{ fontSize: 13 }}>
				Shows active Instant Forms on the Facebook Page linked in Settings →
				Apps.
			</p>
		</div>
	);
};

const FormTemplateSelectorInner: FC<{
	onSelect: (template: MetaAdsLeadFormDraft) => void;
}> = ({ onSelect }) => {
	const { data: forms } = useLeadForms();
	const { loadForm } = useLeadActions();

	if (forms.length === 0) return null;

	const handleSelect = async (formId: string) => {
		const full = await loadForm(formId);
		if (full) onSelect(full);
	};

	return (
		<div>
			<label className="form-label" htmlFor="template-select">
				Use Existing Form Template
			</label>
			<Select
				id="template-select"
				className="form-select form-select-sm"
				defaultValue=""
				onChange={(e) => {
					if (e.target.value) handleSelect(e.target.value);
				}}
			>
				<option value="">-- Select a template --</option>
				{forms.map((f) => (
					<option key={f.id} value={f.id}>
						{f.name}
					</option>
				))}
			</Select>
		</div>
	);
};

const FormTemplateSelector: FC<{
	onSelect: (template: MetaAdsLeadFormDraft) => void;
}> = ({ onSelect }) => {
	return (
		<ErrorBoundary fallback={null}>
			<Suspense fallback={null}>
				<FormTemplateSelectorInner onSelect={onSelect} />
			</Suspense>
		</ErrorBoundary>
	);
};

const SaveAsTemplateButton: FC<{ form: MetaAdsLeadFormDraft }> = ({ form }) => {
	const { saveForm } = useLeadActions();
	const [saved, setSaved] = useState(false);

	const handleSave = async () => {
		await saveForm.mutateAsync(form);
		setSaved(true);
		setTimeout(() => setSaved(false), 2000);
	};

	return (
		<button
			type="button"
			className="btn btn-outline-secondary btn-sm"
			onClick={handleSave}
			disabled={saveForm.isPending}
		>
			{saved ? "Saved!" : "Save as Template"}
		</button>
	);
};
